import { type ComponentProps, type FormEvent } from 'react'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import ChatInputBox from '@/agent-v2/components/ChatInputBox'
import { DictationSurface } from '@/analytics/enums/dictation.enums'
import { selectAgentConversationFormInputText } from '@/agent-v2/chat-form/store/selectors'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'

type AgentV2ChatComposerProps = Omit<
  ComponentProps<typeof ChatInputBox>,
  'inputText' | 'onInputTextChange' | 'onSubmit' | 'onVoiceTranscription' | 'dictationSurface'
> & {
  /* Returns whether the text was submitted, so the input only clears on success. */
  onSubmitText: (text: string) => boolean | Promise<boolean>
  showVoiceInput?: boolean
}

/**
 * The chat's composer: it renders the conversation's unsent text.
 *
 * It's the only part of the chat that reads that text, so typing doesn't re-render the transcript.
 */
export default function AgentV2ChatComposer({
  conversationId,
  onSubmitText,
  textAreaRef,
  showVoiceInput = true,
  ...chatInputBoxProps
}: AgentV2ChatComposerProps) {
  const dispatch = useAppDispatch()
  const inputText = useAppSelector((state: RootState) => selectAgentConversationFormInputText(state, { conversationId }))

  const setInputText = (text: string) => {
    dispatch(AgentConversationFormActions.setInputText({ conversationId, text }))
  }

  const appendVoiceTranscript = (transcript: string) => {
    setInputText(inputText.trim() ? `${inputText.trimEnd()} ${transcript}` : transcript)
  }

  const submitMessage = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    // The box stays editable while a host prepares the submission, so only the text that was sent is cleared.
    if (await onSubmitText(inputText)) dispatch(AgentConversationFormActions.clearSubmittedInputText({ conversationId, submittedText: inputText }))
  }

  return (
    <ChatInputBox
      {...chatInputBoxProps}
      conversationId={conversationId}
      inputText={inputText}
      onInputTextChange={setInputText}
      onSubmit={submitMessage}
      dictationSurface={DictationSurface.chat}
      onVoiceTranscription={showVoiceInput ? appendVoiceTranscript : undefined}
      textAreaRef={textAreaRef}
    />
  )
}
